import { DesignData } from 'editor/src/store/design/types';
import { ExistingVariant } from 'editor/src/store/variants/types';

export interface SpreadsWithoutPagesReport {
  productUid: string;
  spreads: string[];
  removed: boolean;
}

/**
 * Drops the spreads without pages from one design, in place, unless that would leave it with no spreads.
 * Returns what was found, or null when every spread has pages.
 */
export const removeDesignSpreadsWithoutPages = (designData: DesignData): SpreadsWithoutPagesReport | null => {
  if (designData.spreads.every((spread) => spread.pages?.length)) {
    return null;
  }
  const spreadsWithPages = designData.spreads.filter((spread) => spread.pages?.length);
  const report = {
    productUid: designData.product_uid,
    spreads: designData.spreads.filter((spread) => !spread.pages?.length).map((spread) => spread.name),
    removed: spreadsWithPages.length > 0,
  };
  if (spreadsWithPages.length) {
    designData.spreads = spreadsWithPages;
  }
  return report;
};

/**
 * Drops the spreads without pages from each variant design, in place. The other load paths refuse such a spread;
 * when loading a product's variants one bad variant would block the whole product, and the spread has nothing
 * to show or edit. Spread groups address spreads by index, so designs using them are left alone, and a design
 * that would be left with no spreads at all is kept as it is. Returns what was found, for reporting.
 */
const removeSpreadsWithoutPages = (
  existingVariants: ExistingVariant[] | undefined,
  groupedSpreadsPerProductUids: { [productUid: string]: unknown } | undefined,
): SpreadsWithoutPagesReport[] => {
  const reports: SpreadsWithoutPagesReport[] = [];
  existingVariants?.forEach((variant) => {
    const { designData } = variant;
    if (!designData || designData.spread_groups || groupedSpreadsPerProductUids?.[variant.productUid]) {
      return;
    }
    const report = removeDesignSpreadsWithoutPages(designData);
    if (report) {
      reports.push({ ...report, productUid: variant.productUid });
    }
  });
  return reports;
};

export default removeSpreadsWithoutPages;
